import React, { Component } from 'react';
import {
    Card,
    CardBody,
    CardHeader,
    Table,
    Badge,
    Spinner,
    Alert,
    Button,
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Pagination,
    PaginationItem,
    PaginationLink,
    UncontrolledDropdown,
    DropdownToggle,
    DropdownMenu,
    DropdownItem
} from 'reactstrap';
import moment from 'moment';
import { getEmailLogsPaginated, getEmailLogById } from '../../../services/monitoring.service';
import AuthService from '../../Auth/AuthService';
import PermissionFeature from '../../Permissions/PermissionFeature';

/**
 * EmailLogsPanel Component
 *
 * Displays email logs including both sent emails and logged-only emails (when sending was disabled).
 * Shows email subject, service, timestamp, send status, and allows viewing full content.
 *
 * Props:
 * - hours: number (optional) - Number of hours to look back (default: 24)
 * - refreshInterval: number (optional) - Auto-refresh interval in ms (default: 30000)
 */
class EmailLogsPanel extends Component {
    constructor(props) {
        super(props);

        this.state = {
            logs: [],
            loading: true,
            error: null,
            hoursToShow: props.hours || 24,
            selectedLog: null,
            viewModalOpen: false,
            loadingDetail: false,
            // Pagination state
            currentPage: 1,
            pageSize: 10,
            totalCount: 0,
            totalPages: 0
        };

        this.refreshInterval = null;
    }

    componentDidMount() {
        // Check permission before loading data
        const canViewEmailLogs = AuthService.canView(PermissionFeature.MonitoringDashboardViewEmailLogs);

        if (!canViewEmailLogs) {
            this.setState({ loading: false });
            return; // Don't load data if user doesn't have permission
        }

        this.loadLogs();

        // Set up auto-refresh (default: every 30 seconds)
        const refreshInterval = this.props.refreshInterval || 30000;
        this.refreshInterval = setInterval(() => {
            this.loadLogs(true); // Silent refresh
        }, refreshInterval);
    }

    componentDidUpdate(prevProps) {
        // If hours prop changed from parent, update state and reload data
        if (prevProps.hours !== this.props.hours) {
            this.setState({
                hoursToShow: this.props.hours,
                currentPage: 1  // Reset to first page when time range changes
            }, () => {
                this.loadLogs();
            });
        }
    }

    componentWillUnmount() {
        if (this.refreshInterval) {
            clearInterval(this.refreshInterval);
        }
    }

    loadLogs = async (silent = false) => {
        try {
            if (!silent) {
                this.setState({ loading: true, error: null });
            }

            const { hoursToShow, currentPage, pageSize } = this.state;
            const response = await getEmailLogsPaginated(hoursToShow, currentPage, pageSize);

            // Handle both PascalCase (backend default) and camelCase property names
            if (response.Status === 'Success' || response.status === 'Success') {
                const paginatedData = response.Data || response.data || {};
                const logs = paginatedData.Items || paginatedData.items || [];

                this.setState({
                    logs: logs,
                    totalCount: paginatedData.TotalCount || paginatedData.totalCount || 0,
                    totalPages: paginatedData.TotalPages || paginatedData.totalPages || 0,
                    loading: false,
                    error: null
                });

                // Notify parent of total email count (for badge updates)
                if (this.props.onCountUpdate) {
                    this.props.onCountUpdate(paginatedData.TotalCount || paginatedData.totalCount || 0);
                }
            } else {
                throw new Error(response.message || 'Failed to load email logs');
            }
        } catch (error) {
            console.error('Error loading email logs:', error);
            this.setState({
                error: error.message || 'Failed to load email logs',
                loading: false
            });
        }
    };

    handlePageChange = (page) => {
        this.setState({ currentPage: page }, () => {
            this.loadLogs();
        });
    };

    handleTimeRangeChange = (hours) => {
        this.setState({
            hoursToShow: hours,
            currentPage: 1  // Reset to first page when changing time range
        }, () => {
            this.loadLogs();
        });
    };

    getTimeRangeLabel = (hours) => {
        if (hours === 24) return '24 hours';
        if (hours === 168) return '7 days';
        if (hours === 720) return '30 days';
        return `${hours} hours`;
    };

    handleViewEmail = async (emailLogId) => {
        this.setState({ loadingDetail: true, viewModalOpen: true });

        try {
            const response = await getEmailLogById(emailLogId);

            if (response.status === 'Success' && response.data) {
                this.setState({
                    selectedLog: response.data,
                    loadingDetail: false
                });
            } else {
                throw new Error(response.message || 'Failed to load email details');
            }
        } catch (error) {
            console.error('Error loading email details:', error);
            this.setState({
                error: error.message || 'Failed to load email details',
                loadingDetail: false,
                viewModalOpen: false
            });
        }
    };

    toggleViewModal = () => {
        this.setState(prevState => ({
            viewModalOpen: !prevState.viewModalOpen,
            selectedLog: !prevState.viewModalOpen ? null : prevState.selectedLog
        }));
    };

    getSendStatusBadge = (log) => {
        if (log.actuallySent === 1) {
            return <Badge color="success">Sent</Badge>;
        } else if (log.emailEnabled === 0) {
            return <Badge color="warning">Logged Only (Email OFF)</Badge>;
        } else if (log.sendError) {
            return <Badge color="danger">Failed</Badge>;
        } else {
            return <Badge color="secondary">Pending</Badge>;
        }
    };

    renderPagination = () => {
        const { currentPage, totalPages, pageSize, totalCount } = this.state;

        if (totalPages <= 1) return null;

        const pages = [];
        const maxPagesToShow = 5;
        let startPage = Math.max(1, currentPage - Math.floor(maxPagesToShow / 2));
        let endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);

        if (endPage - startPage + 1 < maxPagesToShow) {
            startPage = Math.max(1, endPage - maxPagesToShow + 1);
        }

        for (let i = startPage; i <= endPage; i++) {
            pages.push(i);
        }

        const start = (currentPage - 1) * pageSize + 1;
        const end = Math.min(currentPage * pageSize, totalCount);

        return (
            <div className="d-flex justify-content-between align-items-center mt-3">
                <div className="text-muted small">
                    Showing {start}-{end} of {totalCount} logs
                </div>
                <Pagination size="sm" className="mb-0">
                    <PaginationItem disabled={currentPage === 1}>
                        <PaginationLink previous onClick={() => this.handlePageChange(currentPage - 1)} />
                    </PaginationItem>

                    {startPage > 1 && (
                        <>
                            <PaginationItem>
                                <PaginationLink onClick={() => this.handlePageChange(1)}>1</PaginationLink>
                            </PaginationItem>
                            {startPage > 2 && (
                                <PaginationItem disabled>
                                    <PaginationLink>...</PaginationLink>
                                </PaginationItem>
                            )}
                        </>
                    )}

                    {pages.map(page => (
                        <PaginationItem key={page} active={page === currentPage}>
                            <PaginationLink onClick={() => this.handlePageChange(page)}>
                                {page}
                            </PaginationLink>
                        </PaginationItem>
                    ))}

                    {endPage < totalPages && (
                        <>
                            {endPage < totalPages - 1 && (
                                <PaginationItem disabled>
                                    <PaginationLink>...</PaginationLink>
                                </PaginationItem>
                            )}
                            <PaginationItem>
                                <PaginationLink onClick={() => this.handlePageChange(totalPages)}>
                                    {totalPages}
                                </PaginationLink>
                            </PaginationItem>
                        </>
                    )}

                    <PaginationItem disabled={currentPage === totalPages}>
                        <PaginationLink next onClick={() => this.handlePageChange(currentPage + 1)} />
                    </PaginationItem>
                </Pagination>
            </div>
        );
    };

    render() {
        const { logs, loading, error, viewModalOpen, selectedLog, loadingDetail } = this.state;
        const { standalone = true } = this.props;

        // Check permissions
        const canViewEmailLogs = AuthService.canView(PermissionFeature.MonitoringDashboardViewEmailLogs);
        const canViewEmailContent = AuthService.canView(PermissionFeature.MonitoringDashboardViewEmailContent);

        // If user doesn't have permission to view logs, hide with display:none
        if (!canViewEmailLogs) {
            return <div style={{ display: 'none' }} data-component="EmailLogsPanel" data-reason="No ViewEmailLogs permission" />;
        }

        const content = (
            <div className={standalone ? "" : "p-3"}>
                        {error && (
                            <Alert color="danger">
                                <i className="fa fa-exclamation-triangle mr-2"></i>
                                {error}
                            </Alert>
                        )}

                        {loading && logs.length === 0 ? (
                            <div className="text-center py-4">
                                <Spinner color="primary" />
                                <p className="mt-2 mb-0">Loading email logs...</p>
                            </div>
                        ) : logs.length === 0 ? (
                            <Alert color="info" className="mb-0">
                                <i className="fa fa-info-circle mr-2"></i>
                                No email logs found in the selected time range.
                            </Alert>
                        ) : (
                            <div className="table-responsive" style={{ maxHeight: '600px', overflowY: 'auto' }}>
                                <Table hover size="sm" className="mb-0">
                                    <thead>
                                        <tr>
                                            <th>Timestamp</th>
                                            <th>Service</th>
                                            <th>Subject</th>
                                            <th>Recipient</th>
                                            <th>Status</th>
                                            {canViewEmailContent && <th className="text-center">Actions</th>}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {logs.map(log => (
                                            <tr key={log.emailLogId}>
                                                <td className="text-nowrap">
                                                    {moment.utc(log.createdDate).local().format('MMM D, h:mm A')}
                                                </td>
                                                <td>
                                                    <Badge color="info" pill>
                                                        {log.serviceName}
                                                    </Badge>
                                                </td>
                                                <td>
                                                    <span className="text-truncate d-inline-block" style={{ maxWidth: '300px' }}>
                                                        {log.emailSubject}
                                                    </span>
                                                </td>
                                                <td className="small text-muted">{log.recipientEmail}</td>
                                                <td>{this.getSendStatusBadge(log)}</td>
                                                {canViewEmailContent && (
                                                    <td className="text-center">
                                                        <Button
                                                            color="primary"
                                                            size="xs"
                                                            outline
                                                            onClick={() => this.handleViewEmail(log.emailLogId)}
                                                        >
                                                            <i className="fa fa-eye mr-1"></i>
                                                            View
                                                        </Button>
                                                    </td>
                                                )}
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            </div>
                        )}

                {/* Pagination Controls */}
                {logs.length > 0 && this.renderPagination()}

                {loading && logs.length > 0 && (
                    <div className="text-center mt-2">
                        <small className="text-muted">
                            <Spinner size="sm" color="primary" className="mr-1" />
                            Refreshing...
                        </small>
                    </div>
                )}
            </div>
        );

        const { totalCount } = this.state;

        const cardWrapper = (
            <Card className="mb-3">
                <CardHeader className="bg-secondary text-white d-flex justify-content-between align-items-center">
                    <span>
                        <i className="fa fa-list mr-2"></i>
                        Email Logs
                    </span>
                    <div className="d-flex align-items-center">
                        <UncontrolledDropdown className="mr-2">
                            <DropdownToggle caret color="light" size="sm">
                                <i className="fa fa-clock mr-1"></i>
                                Last {this.getTimeRangeLabel(this.state.hoursToShow)}
                            </DropdownToggle>
                            <DropdownMenu right>
                                <DropdownItem
                                    active={this.state.hoursToShow === 24}
                                    onClick={() => this.handleTimeRangeChange(24)}
                                >
                                    Last 24 hours
                                </DropdownItem>
                                <DropdownItem
                                    active={this.state.hoursToShow === 168}
                                    onClick={() => this.handleTimeRangeChange(168)}
                                >
                                    Last 7 days
                                </DropdownItem>
                                <DropdownItem
                                    active={this.state.hoursToShow === 720}
                                    onClick={() => this.handleTimeRangeChange(720)}
                                >
                                    Last 30 days
                                </DropdownItem>
                            </DropdownMenu>
                        </UncontrolledDropdown>
                        <Badge color="light" pill>
                            {totalCount} {totalCount === 1 ? 'log' : 'logs'}
                        </Badge>
                    </div>
                </CardHeader>
                <CardBody>
                    {content}
                </CardBody>
            </Card>
        );

        return (
            <>
                {standalone ? cardWrapper : content}

                {/* Email Detail View Modal */}
                <Modal isOpen={viewModalOpen} toggle={this.toggleViewModal} size="lg">
                    <ModalHeader toggle={this.toggleViewModal}>
                        <i className="fa fa-envelope mr-2"></i>
                        Email Details
                    </ModalHeader>
                    <ModalBody>
                        {loadingDetail ? (
                            <div className="text-center py-4">
                                <Spinner color="primary" />
                                <p className="mt-2 mb-0">Loading email details...</p>
                            </div>
                        ) : selectedLog ? (
                            <div>
                                <div className="mb-3">
                                    <strong>Subject:</strong>
                                    <div className="border rounded p-2 mt-1 bg-light">
                                        {selectedLog.emailSubject}
                                    </div>
                                </div>

                                <div className="mb-3">
                                    <strong>Service:</strong> <Badge color="info">{selectedLog.serviceName}</Badge>
                                </div>

                                <div className="mb-3">
                                    <strong>Status:</strong> {this.getSendStatusBadge(selectedLog)}
                                </div>

                                {selectedLog.sendError && (
                                    <div className="mb-3">
                                        <strong>Error:</strong>
                                        <Alert color="danger" className="mt-1 mb-0">
                                            {selectedLog.sendError}
                                        </Alert>
                                    </div>
                                )}

                                <div className="mb-3">
                                    <strong>Recipient:</strong> {selectedLog.recipientEmail}
                                </div>

                                <div className="mb-3">
                                    <strong>Timestamp:</strong>{' '}
                                    {moment.utc(selectedLog.createdDate).local().format('MMMM D, YYYY h:mm:ss A')}
                                </div>

                                <div>
                                    <strong>Email Body:</strong>
                                    {(() => {
                                        // Check if content is HTML (starts with <!DOCTYPE or <html)
                                        const isHtml = selectedLog.emailBodyContent &&
                                            (selectedLog.emailBodyContent.trim().toLowerCase().startsWith('<!doctype') ||
                                             selectedLog.emailBodyContent.trim().toLowerCase().startsWith('<html'));

                                        if (isHtml) {
                                            // Render HTML content
                                            return (
                                                <div
                                                    className="border rounded p-3 mt-1 bg-white"
                                                    style={{ maxHeight: '400px', overflowY: 'auto' }}
                                                    dangerouslySetInnerHTML={{ __html: selectedLog.emailBodyContent }}
                                                />
                                            );
                                        } else {
                                            // Render plain text content (backward compatibility)
                                            return (
                                                <div className="border rounded p-3 mt-1 bg-light" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                                                    <pre className="mb-0" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                                        {selectedLog.emailBodyContent}
                                                    </pre>
                                                </div>
                                            );
                                        }
                                    })()}
                                </div>
                            </div>
                        ) : (
                            <Alert color="warning">No email details available</Alert>
                        )}
                    </ModalBody>
                    <ModalFooter>
                        <Button color="secondary" onClick={this.toggleViewModal}>
                            Close
                        </Button>
                    </ModalFooter>
                </Modal>
            </>
        );
    }
}

export default EmailLogsPanel;
